/**
 * Share a listing or a shortlist.
 *
 * Two behaviours behind one button:
 *
 *   navigator.share()  on phones and tablets, which is where a link actually
 *                      gets passed to a group chat. The OS sheet beats
 *                      anything we could build.
 *   clipboard          on desktop, with the label swapping to "Link copied"
 *                      so the click has a visible result. A button that
 *                      silently succeeds reads as a button that failed.
 *
 * The split is by DEVICE, not by whether navigator.share() exists: Chrome and
 * Edge on Windows and macOS have it too, and there it opens a clunky system
 * dialog when the person wanted the link on their clipboard. See canShare().
 *
 * A shortlist has no server-side existence - the saved properties live in
 * localStorage - so its URL is built at click time as /shortlist/?houses=<ids>
 * and the shortlist page renders that list instead of the viewer's own.
 *
 * Both paths tag the URL they hand over (see tagUrl). The GA4 `share` event
 * below counts people PRESSING the button; without a parameter on the link
 * itself, everyone who arrives from a shared link lands as Direct and the
 * other half of the question - whether sharing brings anyone back - cannot be
 * answered at all.
 */

import checkIcon from '@img/icons/check.svg?raw';
import shareIcon from '@img/icons/share.svg?raw';

import { pushToDataLayer } from '@js/src/dataLayer';

const COPIED_MS = 2500;

// Native share only on touch devices. `hover: none` is the media feature that
// separates phones and tablets from desktops: a laptop with a touchscreen still
// has a mouse and reports `hover: hover`, so it copies like any other desktop.
// Feature detection alone is not enough, because desktop Chrome and Edge ship
// navigator.share() and put a system dialog in front of someone who just
// wanted the link.
function canShare() {
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
        return false;
    }

    return typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches;
}

// Clipboard API needs a secure context and is missing in older Safari, so keep
// the execCommand path: the whole point of this button is that it works.
async function copyToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(text);

            return true;
        } catch (error) {
            // Fall through - a rejected permission is not a reason to give up.
        }
    }

    const field = document.createElement('textarea');

    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.top = '-9999px';

    document.body.appendChild(field);
    field.select();

    let copied = false;

    try {
        copied = document.execCommand('copy');
    } catch (error) {
        copied = false;
    }

    document.body.removeChild(field);

    return copied;
}

// GA4's own `share` event, so this can be answered with data in a month rather
// than a hunch. GTM is the transport here (the tag is served first-party
// through Cloudflare), so dataLayer is the channel.
function track(method, button) {
    pushToDataLayer({
        event: 'share',
        method,
        content_type: button.dataset.shareType || 'property',
        item_id: button.dataset.shareId || '',
    }, { immediate: true });
}

function shortlistUrl() {
    // Imported lazily to keep this module usable on pages without the
    // favourites store; the shortlist button only exists where it is loaded.
    const stored = window.localStorage.getItem('lh_favourites');

    let ids = [];

    try {
        ids = (JSON.parse(stored) || [])
            .map((id) => parseInt(id, 10))
            .filter((id) => !isNaN(id))
            .slice(0, 20);
    } catch (error) {
        ids = [];
    }

    if (!ids.length) {
        return '';
    }

    const url = new URL(window.location.origin + window.location.pathname);

    url.searchParams.set('houses', ids.join(','));

    return url.toString();
}

// Parameters that describe how the SHARER arrived, and must never travel on.
// Passing a click ID to a recipient attributes their visit to the sharer's ad
// or campaign, and the ID belongs to one click by one person - it is not ours
// to forward. Every utm_* is stripped too, by prefix, so a stale utm_term from
// a newsletter cannot survive alongside the ones set below.
const INBOUND_PARAMS = [
    'fbclid',
    'gclid',
    'gbraid',
    'wbraid',
    'msclkid',
    'ttclid',
    'igshid',
    'mc_cid',
    'mc_eid',
    'gtm_debug',
    '_gl',
];

/**
 * Tag a URL so the visit it produces is attributable.
 *
 * utm_source=share + utm_medium=referral is what GA4 needs to file this under
 * Referral rather than Direct; campaign carries property vs shortlist, and
 * content carries the mechanism, so "does the OS share sheet bring people back
 * more than a copied link" is answerable rather than a hunch.
 *
 * set() rather than append(): re-sharing a page reached from a shared link
 * would otherwise stack a second set of parameters onto the first.
 */
function tagUrl(rawUrl, button, method) {
    let url;

    try {
        url = new URL(rawUrl, window.location.origin);
    } catch (error) {
        // A URL we cannot parse is still a URL worth sharing.
        return rawUrl;
    }

    // Collected first, deleted after: mutating searchParams while iterating it
    // skips entries.
    const stale = [];

    url.searchParams.forEach((value, key) => {
        const name = key.toLowerCase();

        if (name.startsWith('utm_') || INBOUND_PARAMS.includes(name)) {
            stale.push(key);
        }
    });

    stale.forEach((key) => url.searchParams.delete(key));

    url.searchParams.set('utm_source', 'share');
    url.searchParams.set('utm_medium', 'referral');
    url.searchParams.set('utm_campaign', button.dataset.shareType || 'property');
    url.searchParams.set('utm_content', method);

    return url.toString();
}

function resolveUrl(button) {
    if (button.hasAttribute('data-share-shortlist')) {
        // Re-sharing a list someone sent you shares the same list, not the
        // viewer's own - the page is showing theirs.
        const shared = new URLSearchParams(window.location.search).get('houses');

        return shared ? window.location.href : shortlistUrl();
    }

    return button.dataset.shareUrl || window.location.href;
}

// Copying succeeds silently, and a button that does nothing visible reads as a
// button that failed. The ICON carries the confirmation, not the label: the
// label is `hidden xl:block`, so on any laptop narrower than that - and on
// every phone that fell through to copying - the wording alone would confirm
// nothing. The tick shows at every width; the label and aria-label follow it.
function confirmCopied(button) {
    const label = button.querySelector('[data-share-label]');
    const icon = button.querySelector('.action-button-icon');
    const copied = button.dataset.labelCopied;

    if (!copied) {
        return;
    }

    button.classList.add('active');

    const previousLabel = label ? label.textContent : null;

    if (icon) {
        icon.innerHTML = checkIcon;
    }

    if (label) {
        label.textContent = copied;
    }

    button.setAttribute('aria-label', copied);

    window.setTimeout(() => {
        button.classList.remove('active');

        if (icon) {
            icon.innerHTML = shareIcon;
        }

        if (label && previousLabel !== null) {
            label.textContent = previousLabel;
        }

        if (button.dataset.labelDefault) {
            button.setAttribute('aria-label', button.dataset.labelDefault);
        }
    }, COPIED_MS);
}

export function share() {
    document.querySelectorAll('[data-share]').forEach((button) => {
        if (button.dataset.shareBound) {
            return;
        }

        button.dataset.shareBound = '1';
        button.dataset.labelDefault = button.getAttribute('aria-label') || '';

        button.addEventListener('click', async (event) => {
            event.preventDefault();

            const rawUrl = resolveUrl(button);

            if (!rawUrl) {
                return;
            }

            const title = button.dataset.shareTitle || document.title;

            // A shortlist's wording depends on whose it is. "Houses I've
            // shortlisted" is right when it is yours and wrong the moment
            // someone passes it on, so a list arriving by link gets the
            // neutral form.
            let text = button.dataset.shareText || '';

            if (button.hasAttribute('data-share-shortlist')) {
                text = new URLSearchParams(window.location.search).get('houses')
                    ? button.dataset.shareTextShared || text
                    : text;
            }

            if (canShare()) {
                try {
                    // text as well as url: many targets ignore `title` and
                    // paste only these two, so the sentence that lands in a
                    // group chat is this one.
                    const url = tagUrl(rawUrl, button, 'native');

                    await navigator.share(text ? { title, text, url } : { title, url });
                    track('native', button);

                    return;
                } catch (error) {
                    // AbortError is the user closing the sheet, which is not a
                    // failure and must not fall through to copying a link they
                    // just declined to share.
                    if (error && error.name === 'AbortError') {
                        return;
                    }
                }
            }

            if (await copyToClipboard(tagUrl(rawUrl, button, 'copy'))) {
                confirmCopied(button);
                track('copy', button);
            }
        });
    });
}
